/**
 * SupervisorApprovals — the human-in-the-loop approval queue (lexsup plan §3.7,
 * §6.3 — the approval gate). L6.
 *
 * The supervisor PROPOSES destructive ops (e.g. restart a crash-looping
 * runtime) instead of executing them inline; each proposal is a
 * `supervisorDecisions` doc (status:proposed). This page is where a
 * PLATFORM_ADMIN reviews the queue and Approves (→ lexserver stamps approved +
 * triggers execution; dry-run through Phase 2 so NO real restart fires) or
 * Rejects (→ stamps rejected; never executes). A second panel shows the
 * decided history (executed / rejected / failed).
 *
 * Mirrors the SupervisorOverview / PlatformHealth conventions (Card layout,
 * grey "nothing yet" empties, red error banner). Reached from the
 * SupervisorMonitor launchpad + the Supervisor nav group.
 */

import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Gavel,
  Loader2,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { usePlatformApi } from "../hooks/usePlatformApi";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { PlatformApiError } from "../lib/platform-api";
import type {
  SupervisorDecision,
  SupervisorDecisionStatus,
} from "../types/supervisor";

function fmtTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Render the op params compactly (e.g. `orgId=acme`). */
function fmtParams(params: Record<string, unknown>): string {
  const entries = Object.entries(params ?? {});
  if (entries.length === 0) return "—";
  return entries.map(([k, v]) => `${k}=${String(v)}`).join(", ");
}

const STATUS_BADGE: Record<string, string> = {
  proposed: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  approved: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  executed: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  rejected: "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
};

function StatusBadge({ status }: { status: SupervisorDecisionStatus | string }) {
  return (
    <span
      className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        STATUS_BADGE[status] ?? STATUS_BADGE.proposed
      }`}
    >
      {status}
    </span>
  );
}

export function SupervisorApprovals() {
  const api = usePlatformApi();
  const [pending, setPending] = useState<SupervisorDecision[]>([]);
  const [history, setHistory] = useState<SupervisorDecision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** The decision id currently being approved/rejected (disables its buttons). */
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [proposedRes, allRes] = await Promise.all([
        api.listSupervisorApprovals({ status: "proposed", limit: 100 }),
        api.listSupervisorApprovals({ status: "all", limit: 100 }),
      ]);
      setPending(proposedRes.data.records);
      // History = everything that's no longer pending.
      setHistory(
        allRes.data.records.filter((d) => d.status !== "proposed"),
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load the approval queue");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
    // Poll like SupervisorMonitor (30s) — this is the page where staleness
    // matters most: a proposal landing while the queue is open must appear,
    // and the pending-count badge must not go stale.
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [load]);

  const flashNotice = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 8000);
  };

  const errMsg = (e: unknown): string =>
    e instanceof PlatformApiError
      ? `${e.message} (${e.code})`
      : e instanceof Error
        ? e.message
        : "Action failed";

  const handleApprove = useCallback(
    async (decision: SupervisorDecision) => {
      setBusyId(decision.decision_id);
      try {
        const res = await api.approveSupervisorDecision(decision.decision_id);
        const d = res.data.decision;
        flashNotice(
          `Approved — ${decision.op} is now '${d?.status ?? "executed"}'${
            d?.dry_run ? " (dry-run — no real restart performed)" : ""
          }.`,
        );
        void load();
      } catch (e) {
        flashNotice(`Could not approve: ${errMsg(e)}`);
        void load();
      } finally {
        setBusyId(null);
      }
    },
    [api, load],
  );

  const handleReject = useCallback(
    async (decision: SupervisorDecision) => {
      // eslint-disable-next-line no-alert -- a simple reason prompt is the
      // lightest human-in-the-loop affordance for the MVP approval gate.
      const reason = window.prompt(
        `Reject "${decision.op}" (${fmtParams(decision.params)})?\nOptionally give a reason:`,
        "",
      );
      // prompt returns null on Cancel — abort the reject entirely.
      if (reason === null) return;
      setBusyId(decision.decision_id);
      try {
        await api.rejectSupervisorDecision(decision.decision_id, reason || undefined);
        flashNotice(`Rejected — ${decision.op} will not execute.`);
        void load();
      } catch (e) {
        flashNotice(`Could not reject: ${errMsg(e)}`);
        void load();
      } finally {
        setBusyId(null);
      }
    },
    [api, load],
  );

  return (
    <div className="space-y-6" data-testid="supervisor-approvals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Gavel className="h-5 w-5 text-indigo-500" /> Supervisor Approvals
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Human-in-the-loop queue for the supervisor&apos;s proposed actions.
            Approve to execute (dry-run through Phase 2 — no real restart), or
            reject to decline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div
          className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
          data-testid="supervisor-approvals-error"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {notice && (
        <div
          className="rounded-md bg-indigo-50 p-3 text-sm text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300"
          data-testid="supervisor-approvals-notice"
        >
          {notice}
        </div>
      )}

      {/* Pending queue */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Clock className="h-4 w-4" /> Pending approval
            {pending.length > 0 && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                {pending.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {pending.length === 0 ? (
            <p
              className="py-10 text-center text-sm text-slate-400"
              data-testid="supervisor-approvals-empty"
            >
              {loading ? "Loading…" : "No decisions awaiting approval."}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {pending.map((d) => (
                <li
                  key={d.decision_id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start sm:justify-between"
                  data-testid={`supervisor-approval-${d.decision_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <ShieldAlert className="h-4 w-4 shrink-0 text-amber-500" />
                      <span className="font-mono">{d.op}</span>
                      {d.destructive && (
                        <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-600 dark:bg-red-900/30 dark:text-red-300">
                          destructive
                        </span>
                      )}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      <span className="font-mono">{fmtParams(d.params)}</span>
                    </p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                      {d.rationale}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-400">
                      proposed by <span className="font-mono">{d.proposed_by}</span>{" "}
                      &middot; {fmtTime(d.proposed_at)} &middot; trace{" "}
                      <span className="font-mono">{d.trace_id.slice(0, 12)}</span>
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleApprove(d)}
                      disabled={busyId === d.decision_id}
                      data-testid={`supervisor-approve-${d.decision_id}`}
                      className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {busyId === d.decision_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4" />
                      )}
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReject(d)}
                      disabled={busyId === d.decision_id}
                      data-testid={`supervisor-reject-${d.decision_id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Decided history */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Gavel className="h-4 w-4" /> Decided history
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          {history.length === 0 ? (
            <p
              className="py-10 text-center text-sm text-slate-400"
              data-testid="supervisor-history-empty"
            >
              {loading ? "Loading…" : "No decided decisions yet."}
            </p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  <th className="px-4 py-2.5">Op</th>
                  <th className="px-4 py-2.5">Params</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Dry-run</th>
                  <th className="px-4 py-2.5">Approver</th>
                  <th className="px-4 py-2.5">Decided</th>
                </tr>
              </thead>
              <tbody>
                {history.map((d) => (
                  <tr
                    key={d.decision_id}
                    className="border-b border-slate-100 last:border-0 dark:border-slate-800"
                    data-testid={`supervisor-history-${d.decision_id}`}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs">{d.op}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                      {fmtParams(d.params)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={d.status} />
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {d.dry_run ? (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                          dry-run
                        </span>
                      ) : d.status === "executed" ? (
                        <span className="text-slate-400">live</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{d.approver ?? "—"}</td>
                    <td className="px-4 py-2.5 text-xs">
                      {fmtTime(d.decided_at)}
                      {/* The operator's reject reason is collected by the
                          prompt + persisted as reject_rationale — surface it
                          here so the audit context is actually readable. */}
                      {d.status === "rejected" && d.reject_rationale ? (
                        <div
                          className="mt-0.5 max-w-[28ch] truncate text-[11px] italic text-slate-400"
                          title={d.reject_rationale}
                          data-testid={`supervisor-history-reject-reason-${d.decision_id}`}
                        >
                          “{d.reject_rationale}”
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
